/* eslint-disable spire/export-styles */
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemStyle, GridWidths } from "@insite/mobius/GridItem";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIColors from "@NA/Utils/ADIColors";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    cssClass = "cssClass",
}

export interface RowStyles {
    wrapper?: InjectableCss;
}

export const rowStyles: RowStyles = {
    wrapper: {
        css: css`
            &.feature-brand {
                background-image: linear-gradient(
                    214deg,
                    ${ADIColors.darkBlue2} 3%,
                    ${ADIColors.darkBlue3} 100%
                ); //color not present in style guide
                padding: 40px 17px 40px 17px;
                div[class^="Wrapper"] {
                    background: ${getColor("common.background")};
                    border-radius: 11px;
                    padding-top: 20px;
                    padding-bottom: 34px;
                    h3 {
                        font-size: 34px !important;
                        font-weight: 600;
                        line-height: 1.29;
                        color: ${ADIColors.darkBlue1} !important;
                        margin-top: 16px;
                        margin-bottom: 15px;
                    }
                    div[class^="FlexWrapContainer"] {
                        gap: 10px;
                        div[class^="FlexItem"] {
                            background: ${getColor("common.background")};
                            border-radius: 6px;
                        }
                    }
                }
            }
            &.category-group {
                display: flex;
                flex-wrap: wrap;
                margin-top: -10px;
                margin-bottom: 35px;
                border-bottom-left-radius: 7px;
                border-bottom-right-radius: 7px;
                background-color: ${ADIColors.white2}; // color not present on style guide
                padding: 0;
                > ${GridOffset} {
                    > ${GridItemStyle} {
                        :nth-child(-n + 2) {
                            border-right: solid 1px ${getColor("common.border")};
                        }
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        min-width: 100%;
                                        flex-basis: 100%;
                                        border: solid 1px ${getColor("common.border")};
                                    `,
                                    css`
                                        min-width: 100%;
                                        flex-basis: 100%;
                                        border: solid 1px ${getColor("common.border")};
                                    `,
                                    null,
                                    null,
                                    null,
                                ],
                                "max",
                            )}
                    }
                }
                .categoryctawarpper {
                    margin: 0 auto;
                    padding: 5px 15px 0;
                    display: inline-block;
                    .categoryctaleft {
                        float: left;
                        width: 23%;
                        padding-left: 5px;
                        padding-right: 5px;
                    }
                    .categoryctaRight {
                        float: left;
                        width: 67%;
                        text-align: left;
                        padding-left: 10px;
                        padding-top: 9px;

                        span:first-child {
                            font-size: 20px; // Remove if have same size in style guide
                            font-weight: 800;
                            line-height: normal;
                            color: ${getColor("text.main")};
                            width: 200px;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            width: 100%;
                            display: block;
                        }

                        a .categoryctalearnmore {
                            cursor: pointer;
                            font-size: 16px; // Remove if have same size in style guide
                            font-weight: normal;
                            line-height: normal;
                            color: ${getColor("primary")};
                            display: block;
                            :hover {
                                text-decoration: underline;
                            }
                            ::after {
                                color: ${getColor("primary")};
                                font-weight: 900;
                                width: 6px;
                                display: inline-block;
                                font: normal normal normal 6px/1 FontAwesome;
                                font-size: 17px;
                                text-rendering: auto;
                                -webkit-font-smoothing: antialiased;
                                -moz-osx-font-smoothing: grayscale;
                                transform: translate(0, 0);
                                content: "›";
                                margin-left: 5px;
                            }
                        }
                    }
                }
            }
            &.shop-products {
                > ${GridOffset} {
                    > ${GridItemStyle}:first-child {
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    display: none;
                                `,
                                css`
                                    display: none;
                                `,
                                css`
                                    display: none;
                                `,
                                css`
                                    max-width: 261px !important;
                                    width: 261px !important;
                                    flex-basis: 261px !important;
                                `,
                                css`
                                    max-width: 261px !important;
                                    width: 261px !important;
                                    flex-basis: 261px !important;
                                `,
                            ])}
                    }
                    > ${GridItemStyle}:nth-child(2) {
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    flex-basis: 100% !important;
                                    max-width: 100% !important;
                                `,
                                css`
                                    flex-basis: 100% !important;
                                    max-width: 100% !important;
                                `,
                                css`
                                    flex-basis: 100% !important;
                                    max-width: 100% !important;
                                `,
                                css`
                                    flex-basis: calc(100% - 271px) !important;
                                    max-width: calc(100% - 271px) !important;
                                    width: calc(100% - 271px) !important;
                                `,
                                css`
                                    flex-basis: calc(100% - 271px) !important;
                                    max-width: calc(100% - 271px) !important;
                                    width: calc(100% - 271px) !important;
                                `,
                            ])}
                    }
                }
            }
            &.signInDrawer {
                h4 {
                    padding: 0;
                    margin: 20px;
                    font-size: 24px;
                    font-weight: bold;
                    font-stretch: normal;
                    font-style: normal;
                    line-height: 1.43;
                    letter-spacing: normal;
                    text-align: left;
                    color: ${getColor("text.main")};
                    cursor: default;
                }
                form {
                    padding: 0 20px 0 20px;
                    margin-top: 6px;
                    float: left;
                    width: 100%;

                    > ${GridWrapper} {
                        > ${GridOffset} {
                            margin: auto;
                            width: 100%;
                            > ${GridItemStyle} {
                                width: 100%;
                                padding: 0px;
                                margin-bottom: 15px;
                                position: relative;
                                label {
                                    margin-top: 0;
                                    font-size: 14px;
                                    font-weight: bold;
                                    font-stretch: normal;
                                    font-style: normal;
                                    line-height: normal;
                                    letter-spacing: normal;
                                    text-align: left;
                                    color: ${getColor("text.main")};
                                }
                                input {
                                    border-radius: 6px;
                                    padding: 9px 15px;
                                    margin: 0;
                                    border: 1px solid #bbb; // color not present in style guide
                                    font-size: 14px;
                                    font-weight: normal;
                                    line-height: 1.43;
                                }
                                button[data-test-selector^="signIn_forgotPassword"] {
                                    &:hover {
                                        > ${TypographyStyle} {
                                            text-decoration: underline;
                                        }
                                    }
                                    > ${TypographyStyle} {
                                        font-size: 12px;
                                        font-weight: bold;
                                        font-stretch: normal;
                                        font-style: normal;
                                        line-height: 1.67;
                                        letter-spacing: normal;
                                        color: ${getColor("secondary")};
                                        text-transform: none;
                                    }
                                }
                                #rememberMe-label {
                                    cursor: pointer;

                                    user-select: none;
                                    display: inline-block;
                                    font-size: 12px;
                                    font-weight: normal;
                                    font-stretch: normal;
                                    font-style: normal;
                                    line-height: normal;
                                    letter-spacing: normal;
                                    text-align: left;
                                    color: ${getColor("text.main")};
                                }
                                button[data-test-selector^="signIn_submit"] {
                                    background-color: ${getColor("primary")};
                                    border-radius: 4px;
                                    line-height: normal;
                                    margin-bottom: 1rem;
                                    > span {
                                        font-size: 16px;
                                        font-weight: bold;
                                    }
                                }
                            }
                        }
                    }
                }
            }
            &.pdpAddToCartBtnWrapper {
                width: 100%;
                display: flex;
                justify-content: center;
                padding-top: 15px;
                > div {
                    button {
                        border-radius: 4px;
                    }
                    @media (max-width: 767px) {
                        max-width: 100%;
                        > [class^="GridItemStyle"]:nth-child(1) {
                            width: 100px;
                            flex-basis: 100px;
                            max-width: 100px;
                        }
                        > [class^="GridItemStyle"]:nth-child(2) {
                            width: calc(100% - 100px);
                            flex-basis: calc(100% - 100px);
                            max-width: calc(100% - 100px);
                        }
                    }
                }
                @media (min-width: 768px) and (max-width: 1079px) {
                    > [class^="GridItemStyle"] > [class^="Wrapper"] button {
                        padding: 0px 5px;
                    }
                }
            }
            &.leftNavDisableOnMobile {
                > ${GridOffset} {
                    > ${GridItemStyle}:first-child {
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    display: none;
                                `,
                                css`
                                    display: none;
                                `,
                                css`
                                    display: none;
                                `,
                                null,
                                null,
                            ])}
                    }
                    > ${GridItemStyle}:nth-child(2) {
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    flex-basis: 100%;
                                    max-width: 100%;
                                `,
                                css`
                                    flex-basis: 100%;
                                    max-width: 100%;
                                `,
                                css`
                                    flex-basis: 100%;
                                    max-width: 100%;
                                `,
                                null,
                                null,
                            ])}
                    }
                }
            }
            &.mini-banner-mobile {
                display: none;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: none;
                            `,
                        ],
                        "max",
                    );
                }}

                @media (max-width: 767px) {
                    display: block !important;
                }
            }
            &.mini-banner-desktop {
                display: block;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: none;
                            `,
                        ],
                        "max",
                    );
                }}
            }
            &.rpp-wrapper {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            background: #f7f7f7;
                        `,
                        css`
                            background: #f7f7f7;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
            &.rpp-inner-container {
                > ${GridOffset} {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                align-items: end;
                                margin-bottom: 20px;
                            `,
                            css`
                                align-items: end;
                                margin-bottom: 20px;
                            `,
                            null,
                            null,
                            null,
                        ])}
                }
            }
        `,
    },
};

const styles = rowStyles;

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.cssClass]: string;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const Row: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: { columns, gap, alignment, reflow, cssClass },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem key={x} width={reflow ? convertToResponsive(columns[x]) : columns[x]} align={align}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    return (
        <GridContainer className={cssClass} gap={gap || 0} {...styles.wrapper}>
            {columnElements}
        </GridContainer>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        displayName: "ADI Custome Row",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
